import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { styled } from 'styled-components';

import { getIssueType, editIssueType } from '../services/IssueTypesService';
import { useAppContext } from '../contextApi/context';
import { ISSUE_TYPE_NAME_DUPLICATED } from '../constants/apiMessages';
import { ToggleOption } from '../styles/StyledComponents';

const EditIssueType = () => {
    // ----- Context/Hooks
    const { loggedUser } = useAppContext();
    const { id } = useParams();

    // ----- State
    const [currentIssueType, setCurrentIssueType] = useState(null);
    const [vehicleRequired, setVehicleRequired] = useState(null);
    const [showIssueTypeEdited, setShowIssueTypeEdited] = useState(false);
    const [showErrorOnEdit, setShowErrorOnEdit] = useState(false);
    const [showErrorDuplicatedIssueType, setShowErrorDuplicatedIssueType] = useState(false);

    // ----- API Fetch Actions
    const fetchIssueTypeData = useCallback(async () => {
        return await getIssueType(loggedUser.token, id);
    }, [loggedUser.token, id]);

    // ----- Hooks
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            name: '',
            description: '',
        },
    });

    useEffect(() => {
        fetchIssueTypeData().then((data) => {
            setCurrentIssueType(data);
            setVehicleRequired(data.vehicleRequired ?? true);
            const { name, description } = data;
            reset({ name, description });
        });
    }, [fetchIssueTypeData, reset]);

    // ----- Actions
    const handleEdit = async (data) => {
        clearMessages();

        const formData = {
            ...data,
            vehicleRequired,
            requesterRequired: true,
        };

        const response = await editIssueType(loggedUser.token, currentIssueType.id, formData);

        if (response.status === 200) {
            setShowIssueTypeEdited(true);
        } else {
            // Error from API
            const { data } = response;

            if (data.includes(ISSUE_TYPE_NAME_DUPLICATED)) {
                setShowErrorDuplicatedIssueType(true);
            } else {
                setShowErrorOnEdit(true);
            }
        }
    };

    const handleVehicleRequiredChange = (e) => {
        const isVehicleRequired = e.target.value === 'true';
        setVehicleRequired(isVehicleRequired);
    };

    const clearMessages = () => {
        setShowIssueTypeEdited(false);
        setShowErrorOnEdit(false);
        setShowErrorDuplicatedIssueType(false);
    };

    // ----- Render
    return (
        <div className='container mt-4'>
            {showIssueTypeEdited && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Tipo de despacho editado satisfactoriamente.
                </div>
            )}

            {showErrorOnEdit && (
                <div className='alert alert-danger text-center mb-1' role='alert'>
                    Tipo de despacho no editado.
                </div>
            )}

            {showErrorDuplicatedIssueType && (
                <div className='alert alert-danger text-center mb-1' role='alert'>
                    El nombre del tipo de despacho ya se encuentra registrado.
                </div>
            )}

            <div className='mb-3'>
                <Link to='/tipos-despacho'>Volver a página de tipos de despacho</Link>
            </div>

            <h1>Editar Tipo de Despacho</h1>
            <FormContainer>
                <form onSubmit={handleSubmit(handleEdit)}>
                    <label className='d-flex mb-1' htmlFor='name'>
                        Nombre
                    </label>
                    <input
                        className='form-control'
                        type='text'
                        id='name'
                        autoComplete='off'
                        maxLength={50}
                        {...register('name', { required: true, maxLength: 50 })}
                        autoFocus
                    />
                    <span className='my-1' role='alert'>
                        {errors.name?.type === 'required' && `Campo requerido`}
                        {errors.name?.type === 'maxLength' && `Formato inválido`}
                    </span>

                    <label className='d-flex mb-1' htmlFor='description'>
                        Descripción
                    </label>
                    <textarea
                        className='form-control'
                        id='description'
                        rows='3'
                        maxLength={100}
                        {...register('description', { required: true, maxLength: 100 })}
                    />
                    <span className='my-1' role='alert'>
                        {errors.description?.type === 'required' && `Campo requerido`}
                        {errors.description?.type === 'maxLength' && `Formato inválido`}
                    </span>

                    <label className='d-flex mb-1'>Vehículo requerido</label>
                    <ToggleOption className='mb-3'>
                        <input
                            type='radio'
                            className='btn-check'
                            name='vehicleRequired'
                            id='vehicleYes'
                            value='true'
                            onChange={handleVehicleRequiredChange}
                            checked={vehicleRequired === true}
                        />
                        <label className='btn btn-outline-success first-option' htmlFor='vehicleYes'>
                            Si
                        </label>

                        <input
                            type='radio'
                            className='btn-check'
                            name='vehicleRequired'
                            id='vehicleNo'
                            value='false'
                            onChange={handleVehicleRequiredChange}
                            checked={vehicleRequired === false}
                        />
                        <label className='btn btn-outline-success second-option' htmlFor='vehicleNo'>
                            No
                        </label>
                    </ToggleOption>

                    <div className='d-flex justify-content-center'>
                        <button className='btn btn-danger mt-3' type='submit'>
                            Guardar cambios
                        </button>
                    </div>
                </form>
            </FormContainer>
        </div>
    );
};

const FormContainer = styled.div`
    margin: 25px auto 0;
    padding: 40px 40px 20px;
    width: 70%;

    form {
        display: grid;

        > label {
            font-size: 15px;
            font-weight: bold;
        }

        > span {
            color: #d04437;
            font-size: 13px;
            min-height: 12px;
        }

        button {
            background-color: #d04437;
            width: 30%;
        }

        > textarea {
            resize: none;
        }
    }
`;

export default EditIssueType;
